import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext.js';
import { ServerRegistryService } from '@/lib/serverRegistry.js';
import { getApiUrl } from '@/lib/api-url.js';
import type { ServerRegistryEntry } from '@/types/registry.js';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import {
  Search,
  Plug,
  Loader2,
  AlertCircle,
  Package,
  ExternalLink,
  Star,
  ArrowLeft,
  Download,
  Trash2,
  Info,
  Plus,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils.js';

interface ConnectorSelectorModalProps {
  open: boolean;
  onClose: () => void;
}

const serverRegistry = ServerRegistryService.getInstance();

export function ConnectorSelectorModal({
  open,
  onClose,
}: ConnectorSelectorModalProps) {
  const { authenticatedFetch } = useAuth();
  const [entries, setEntries] = useState<ServerRegistryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedEntry, setSelectedEntry] = useState<ServerRegistryEntry | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('discover');
  const [customName, setCustomName] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customType, setCustomType] = useState<'http' | 'sse'>('sse');
  const [customHeaders, setCustomHeaders] = useState<Array<{ key: string; value: string }>>([]);

  useEffect(() => {
    if (open) {
      loadRegistry();
    } else {
        setSearchQuery('');
        setSelectedEntry(null);
        setCategoryFilter('all');
        setCustomName('');
        setCustomUrl('');
        setCustomType('sse');
        setCustomHeaders([]);
    }
  }, [open]);

  const loadRegistry = async () => {
    try {
      setLoading(true);
      setError(null);
      await serverRegistry.syncWithServerStatus(authenticatedFetch);
      const allEntries = await serverRegistry.getEntries();
      setEntries(allEntries);
    } catch (err) {
      console.error('Failed to load connector registry:', err);
      setError(err instanceof Error ? err.message : 'Failed to load connectors');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (entry: ServerRegistryEntry, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      setConnectingId(entry.id);
      setError(null);

      await authenticatedFetch(`${getApiUrl()}/mcp/servers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: entry.id,
          config: entry.config,
        }),
      }).then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to connect connector');
        }
        return response.json();
      });

      serverRegistry.setInstalled(entry.id, true);
      await loadRegistry();

      if (selectedEntry && selectedEntry.id === entry.id) {
          setSelectedEntry({ ...entry, isInstalled: true });
      }
    } catch (err) {
      console.error('Failed to connect:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect to connector');
    } finally {
      setConnectingId(null);
    }
  };

  const handleDisconnect = async (entry: ServerRegistryEntry, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      setConnectingId(entry.id);
      setError(null);

      await authenticatedFetch(`${getApiUrl()}/mcp/servers/${entry.id}`, {
        method: 'DELETE',
      }).then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to disconnect connector');
        }
      });

      serverRegistry.setInstalled(entry.id, false);
      await loadRegistry();

      if (selectedEntry && selectedEntry.id === entry.id) {
         setSelectedEntry({ ...entry, isInstalled: false });
      }
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect connector');
    } finally {
      setConnectingId(null);
    }
  };

  const handleAddCustom = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!customName.trim() || !customUrl.trim()) {
      setError('Please provide both name and URL');
      return;
    }

    try {
      const customId = `custom-${customName.toLowerCase().replace(/\s+/g, '-')}`;
      setConnectingId(customId);
      setError(null);

      // Build config with optional headers
      const config: Record<string, unknown> = {
        type: customType,
        url: customUrl,
      };

      // Add headers if any are provided
      const validHeaders = customHeaders.filter(h => h.key.trim() && h.value.trim());
      if (validHeaders.length > 0) {
        const headersObj: Record<string, string> = {};
        validHeaders.forEach(h => {
          headersObj[h.key.trim()] = h.value.trim();
        });
        config.headers = headersObj;
      }

      await authenticatedFetch(`${getApiUrl()}/mcp/servers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customId,
          config,
        }),
      }).then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to add custom connector');
        }
        return response.json();
      });

      // Reset form
      setCustomName('');
      setCustomUrl('');
      setCustomType('sse');
      setCustomHeaders([]);

      // Reload registry
      await loadRegistry();

      // Switch to installed tab to show the new connector
      setActiveTab('installed');
    } catch (err) {
      console.error('Failed to add custom connector:', err);
      setError(err instanceof Error ? err.message : 'Failed to add custom connector');
    } finally {
      setConnectingId(null);
    }
  };


  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      // Filter out non-SSE/HTTP types
      const isSupportedType = entry.config.type === 'sse' || entry.config.type === 'http';
      if (!isSupportedType) return false;

      const query = searchQuery.toLowerCase();
      const matchesSearch =
        entry.name.toLowerCase().includes(query) ||
        entry.description.toLowerCase().includes(query) ||
        entry.tags.some((tag) => tag.toLowerCase().includes(query));

      const matchesCategory =
        categoryFilter === 'all' || entry.category === categoryFilter;

      const matchesTab =
        activeTab === 'discover' ? true :
        activeTab === 'installed' ? entry.isInstalled : true;

      return matchesSearch && matchesCategory && matchesTab;
    });
  }, [entries, searchQuery, categoryFilter, activeTab]);

  // Get available categories based on supported entries
  const availableCategories = useMemo(() => {
    const supportedEntries = entries.filter((entry) => {
      const isSupportedType = entry.config.type === 'sse' || entry.config.type === 'http';
      return isSupportedType;
    });

    const categoriesInUse = new Set(supportedEntries.map((e) => e.category as string));

    const allCategories = [
      { value: 'all', label: 'All' },
      { value: 'productivity', label: 'Productivity' },
      { value: 'development', label: 'Development' },
      { value: 'research', label: 'Research' },
      { value: 'creative', label: 'Creative' },
      { value: 'data', label: 'Data' },
      { value: 'communication', label: 'Communication' },
      { value: 'custom', label: 'Custom' },
    ];

    return allCategories.filter((cat) => cat.value === 'all' || categoriesInUse.has(cat.value));
  }, [entries]);

  // Reset category filter if it's no longer available
  useEffect(() => {
    if (categoryFilter !== 'all') {
      const isAvailable = availableCategories.some((cat) => cat.value === categoryFilter);
      if (!isAvailable) {
        setCategoryFilter('all');
      }
    }
  }, [availableCategories, categoryFilter]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-3xl h-[100dvh] sm:h-[90vh] flex flex-col p-0 gap-0 overflow-hidden rounded-none sm:rounded-lg bg-background">
        {/* Fixed Header */}
        <div className="border-b px-4 sm:px-6 py-4 bg-background flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {selectedEntry ? (
              <Button variant="ghost" size="icon-sm" onClick={() => setSelectedEntry(null)} className="-ml-1">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            ) : (
              <div className="p-2 bg-primary/10 rounded">
                <Package className="h-5 w-5 text-primary" />
              </div>
            )}
            <div>
              <DialogTitle className="text-lg font-semibold">
                {selectedEntry ? selectedEntry.name : 'Connectors'}
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">
                {selectedEntry ? 'Connector Details' : 'Manage your MCP integrations'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs Section - Fixed */}
        {!selectedEntry && (
          <div className="border-b px-4 sm:px-6 py-3 bg-background flex-shrink-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-fit">
              <TabsList className="grid grid-cols-3 w-full sm:w-auto">
                <TabsTrigger value="discover" className="text-sm">Discover</TabsTrigger>
                <TabsTrigger value="installed" className="text-sm">Installed</TabsTrigger>
                <TabsTrigger value="custom" className="text-sm">Custom</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Search & Filters Section - Fixed */}
        {!selectedEntry && activeTab !== 'custom' && (
          <div className="border-b px-4 sm:px-6 py-3 bg-background/95 flex-shrink-0 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search connectors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background border-border/50"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-1 px-1">
              {availableCategories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setCategoryFilter(cat.value)}
                  className={cn(
                    "text-xs px-3 py-1.5 rounded-full whitespace-nowrap transition-all",
                    categoryFilter === cat.value
                      ? "bg-primary text-primary-foreground font-medium"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted"
                  )}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto bg-muted/20 p-4 sm:p-6">
            {error && (
                <div className="mb-4 p-3 rounded bg-destructive/10 text-destructive text-sm flex items-center gap-2 border border-destructive/20">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {activeTab === 'custom' ? (
              /* Custom MCP Form */
              <div className="max-w-2xl mx-auto">
                <div className="bg-card border rounded-lg p-4 sm:p-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Plug className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">Add Custom MCP Server</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Connect to your own HTTP or SSE MCP server
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleAddCustom} className="space-y-5">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Server Name <span className="text-destructive">*</span>
                      </label>
                      <Input
                        placeholder="My Custom Server"
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        required
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">
                        A friendly name to identify this server
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Server URL <span className="text-destructive">*</span>
                      </label>
                      <Input
                        placeholder="https://example.com/mcp or http://localhost:3000/sse"
                        value={customUrl}
                        onChange={(e) => setCustomUrl(e.target.value)}
                        required
                        type="url"
                        className="bg-background font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        The full URL endpoint for your MCP server
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Protocol Type <span className="text-destructive">*</span>
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setCustomType('sse')}
                          className={cn(
                            "flex-1 py-2.5 px-3 rounded border-2 transition-all text-sm",
                            customType === 'sse'
                              ? "border-primary bg-primary/5 text-primary"
                              : "border-border bg-background text-muted-foreground hover:border-primary/30"
                          )}
                        >
                          <div className="font-medium">SSE</div>
                          <div className="text-xs mt-0.5 font-normal opacity-70">Server-Sent Events</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomType('http')}
                          className={cn(
                            "flex-1 py-2.5 px-3 rounded border-2 transition-all text-sm",
                            customType === 'http'
                              ? "border-primary bg-primary/5 text-primary"
                              : "border-border bg-background text-muted-foreground hover:border-primary/30"
                          )}
                        >
                          <div className="font-medium">HTTP</div>
                          <div className="text-xs mt-0.5 font-normal opacity-70">Standard HTTP</div>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        HTTP Headers <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                      </label>
                      <div className="space-y-2">
                        {customHeaders.map((header, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              placeholder="Header name (e.g., Authorization)"
                              value={header.key}
                              onChange={(e) => {
                                const newHeaders = [...customHeaders];
                                newHeaders[index].key = e.target.value;
                                setCustomHeaders(newHeaders);
                              }}
                              className="flex-1 bg-background font-mono text-sm"
                            />
                            <Input
                              placeholder="Header value"
                              value={header.value}
                              onChange={(e) => {
                                const newHeaders = [...customHeaders];
                                newHeaders[index].value = e.target.value;
                                setCustomHeaders(newHeaders);
                              }}
                              className="flex-1 bg-background font-mono text-sm"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setCustomHeaders(customHeaders.filter((_, i) => i !== index));
                              }}
                              className="flex-shrink-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCustomHeaders([...customHeaders, { key: '', value: '' }])}
                          className="w-full"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Header
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Add custom HTTP headers for authentication or other purposes
                      </p>
                    </div>

                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={connectingId !== null || !customName.trim() || !customUrl.trim()}
                        className="w-full gap-2"
                        size="lg"
                      >
                        {connectingId !== null ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <Plug className="h-4 w-4" />
                            Add Custom Server
                          </>
                        )}
                      </Button>
                    </div>
                  </form>

                  <div className="pt-4 border-t">
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <p>
                        Make sure your server implements the MCP (Model Context Protocol) specification
                        and is accessible from this application.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : loading ? (
                <div className="h-full flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-primary/50" />
                        <p className="text-sm text-muted-foreground">Loading registry...</p>
                    </div>
                </div>
            ) : selectedEntry ? (
                /* Detail View - App Store Style */
                <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-200">
                    {/* Header with Icon, Title, Badges, and Action Button */}
                    <div className="flex items-start gap-6">
                        <div className="w-24 h-24 rounded-2xl bg-card border shadow-sm flex items-center justify-center flex-shrink-0">
                            <Plug className="h-10 w-10 text-primary/80" />
                        </div>

                        <div className="flex-1 space-y-3">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <h2 className="text-3xl font-bold text-foreground">{selectedEntry.name}</h2>
                                    <p className="text-sm text-muted-foreground mt-1">{selectedEntry.author || 'Unknown Author'}</p>
                                </div>

                                {/* Action Button - Top Right */}
                                {selectedEntry.isInstalled ? (
                                    <Button
                                        variant="outline"
                                        className="text-destructive hover:text-destructive hover:bg-destructive/5 gap-2 whitespace-nowrap"
                                        onClick={() => handleDisconnect(selectedEntry)}
                                        disabled={connectingId === selectedEntry.id}
                                        size="sm"
                                    >
                                        {connectingId === selectedEntry.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                        Uninstall
                                    </Button>
                                ) : (
                                    <Button
                                        className="gap-2 whitespace-nowrap"
                                        onClick={() => handleConnect(selectedEntry)}
                                        disabled={connectingId === selectedEntry.id}
                                        size="lg"
                                    >
                                        {connectingId === selectedEntry.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                        Install
                                    </Button>
                                )}
                            </div>

                            {/* Badges */}
                            <div className="flex flex-wrap gap-2 pt-2">
                                <Badge variant="outline" className="capitalize">{selectedEntry.category}</Badge>
                                {selectedEntry.isOfficial && (
                                    <Badge variant="secondary" className="gap-1 bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
                                        <Star className="h-3 w-3 fill-current" /> Official
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="border-t pt-6">
                        <p className="text-base text-muted-foreground leading-relaxed">
                            {selectedEntry.description}
                        </p>
                    </div>

                    {/* Details Section */}
                    <div className="border-t pt-6 space-y-4">
                        <h3 className="font-semibold text-foreground">Information</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center py-2 border-b border-border/50">
                                <span className="text-muted-foreground">Type</span>
                                <span className="font-mono text-xs bg-muted/50 px-2 py-1 rounded">{selectedEntry.config.type}</span>
                            </div>
                            {selectedEntry.author && (
                                <div className="flex justify-between items-center py-2 border-b border-border/50">
                                    <span className="text-muted-foreground">Author</span>
                                    <span className="font-medium">{selectedEntry.author}</span>
                                </div>
                            )}
                            {selectedEntry.homepage && (
                                <a
                                    href={selectedEntry.homepage}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-primary hover:underline py-2"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    Visit Homepage
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Tags */}
                    {selectedEntry.tags.length > 0 && (
                        <div className="border-t pt-6 space-y-3">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Tags</h3>
                            <div className="flex flex-wrap gap-2">
                                {selectedEntry.tags.map(tag => (
                                    <Badge key={tag} variant="secondary" className="bg-muted text-muted-foreground border-0">{tag}</Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                /* Grid View */
                <>
                    {filteredEntries.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                          <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center">
                            <Search className="h-10 w-10 text-muted-foreground/40" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-foreground">No connectors found</h3>
                            <p className="text-muted-foreground text-sm max-w-md mt-2">
                              {searchQuery || categoryFilter !== 'all'
                                ? "Try adjusting your search terms or filters to find what you're looking for."
                                : activeTab === 'installed'
                                ? "You haven't installed any connectors yet."
                                : "No connectors available right now."}
                            </p>
                          </div>
                          {activeTab === 'installed' && (
                            <Button
                              variant="outline"
                              onClick={() => setActiveTab('discover')}
                              className="mt-4"
                            >
                              Browse all connectors
                            </Button>
                          )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {filteredEntries.map(entry => (
                                <div
                                    key={entry.id}
                                    onClick={() => setSelectedEntry(entry)}
                                    className="group relative flex flex-col bg-card border rounded-lg transition-all cursor-pointer hover:shadow-sm hover:border-primary/20 active:scale-[0.98]"
                                >
                                    <div className="p-3 sm:p-4 pb-0">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="w-9 h-9 rounded bg-muted/50 flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                                                <Plug className="h-4 w-4" />
                                            </div>
                                            {entry.isOfficial && (
                                                <div className="text-yellow-500 dark:text-amber-400" title="Official Connector">
                                                    <Star className="h-3.5 w-3.5 fill-current" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="mb-3">
                                            <h4 className="font-medium text-sm text-foreground mb-0.5 group-hover:text-primary transition-colors">
                                                {entry.name}
                                            </h4>
                                            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                                                {entry.description}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-auto px-3 sm:px-4 py-2.5 bg-muted/30 border-t flex items-center gap-2 rounded-b-lg">
                                        <div className="flex-1 text-xs text-muted-foreground capitalize truncate">
                                            {entry.category}
                                        </div>

                                        {entry.isInstalled ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs px-3 min-w-20 transition-all duration-200 text-destructive hover:text-destructive hover:bg-destructive/5"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDisconnect(entry);
                                                }}
                                                disabled={connectingId === entry.id}
                                            >
                                              {connectingId === entry.id ? (
                                                <div className="flex items-center gap-1">
                                                  <Loader2 className="h-3 w-3 animate-spin" />
                                                </div>
                                              ) : (
                                                'Uninstall'
                                              )}
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant={connectingId === entry.id ? "default" : "outline"}
                                                className={cn(
                                                  "h-7 text-xs px-3 min-w-20 transition-all duration-200",
                                                  connectingId === entry.id && "bg-primary text-primary-foreground hover:bg-primary/90"
                                                )}
                                                onClick={(e) => handleConnect(entry, e)}
                                                disabled={connectingId === entry.id}
                                            >
                                              {connectingId === entry.id ? (
                                                <div className="flex items-center gap-1">
                                                  <Loader2 className="h-3 w-3 animate-spin" />
                                                  <span>Installing</span>
                                                </div>
                                              ) : (
                                                'Install'
                                              )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
